import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { connect } from 'react-redux';

import Cross from '@o2/icons/Cross';
import { styled } from '@mui/material';
import Box from '@o2/ui/Box';
import Button from '@o2/ui/Button';
import Typography from '@o2/ui/Typography';

import {
  IS_ADOBE_ANALYTICS_ENABLED_PROPERTY,
  isAdobeAnalyticsEnabled,
  selectAdobeAnalyticsData,
} from 'saga/adobeAnalytics/ducks';
import Modal from 'views/components/Modal/Modal';

const ModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: 'rgb(237, 239, 246)',

  [theme.breakpoints.down('sm')]: {
    padding: `${theme.spacing(4)} ${theme.spacing(2.5)}`,
  },
}));

const ButtonsWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const ScrollablePre = styled('pre')(() => ({
  border: '1px solid rgba(0, 0, 0, 0.15)',
  backgroundColor: 'white',
  whiteSpace: 'pre-wrap',
  padding: '10px',
  fontFamily: 'monospace',
  height: '340px',
  maxHeight: '340px',
  overflowY: 'scroll',
}));

const AdobeAnalyticsModal = ({ adobeAnalyticsData, adobeAnalyticsEnabled }: StateToProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const prettyPrintData = JSON.stringify(adobeAnalyticsData, null, 4);

  useHotkeys('ctrl+alt+a', (e: any) => {
    e.preventDefault();
    setModalOpen(true);
  });

  const closeModal = () => setModalOpen(false);

  const copyToClipboard = () => navigator.clipboard.writeText(prettyPrintData);

  return (
    <Modal open={modalOpen} onClose={closeModal}>
      <ModalContent>
        <Box>
          <Typography variant="h2" align="center">
            Adobe Analytics data
          </Typography>
        </Box>
        <ScrollablePre>
          {adobeAnalyticsEnabled
            ? prettyPrintData
            : `Adobe Analytics is disabled (see property: ${IS_ADOBE_ANALYTICS_ENABLED_PROPERTY}).`}
        </ScrollablePre>
        <ButtonsWrapper>
          <Button variant="contained" onClick={copyToClipboard} disabled={!adobeAnalyticsEnabled}>
            Copy to clipboard
          </Button>
          <Button variant="outlined" onClick={closeModal} endIcon={<Cross />}>
            Close
          </Button>
        </ButtonsWrapper>
      </ModalContent>
    </Modal>
  );
};

interface StateToProps {
  adobeAnalyticsData?: any[];
  adobeAnalyticsEnabled?: boolean;
}

const mapStateToProps = (): StateToProps => ({
  adobeAnalyticsData: selectAdobeAnalyticsData(),
  adobeAnalyticsEnabled: isAdobeAnalyticsEnabled(),
});

export default connect(mapStateToProps, null)(AdobeAnalyticsModal);
